import type { BoxProps, ButtonProps } from '@mantine/core'
import { Box, Button, Group, Progress, Text } from '@mantine/core'
import type { DropzoneProps } from '@mantine/dropzone'
import { Dropzone, IMAGE_MIME_TYPE } from '@mantine/dropzone'
import type { IconProps } from '@tabler/icons-react'
import { IconArrowDown, IconCloudUpload, IconX } from '@tabler/icons-react'
import { inflect } from 'inflection'
import type { ComponentType, ReactNode } from 'react'
import { useRef } from 'react'

import type { EmptyStateProps } from '@/components/EmptyState'
import { EmptyState } from '@/components/EmptyState'
import { InputLabel } from '@/components/InputLabel'
import { theme } from '@/configs/theme'
import * as classes from '@/styles/FileUpload.css'

const pdfMimeType = 'application/pdf'

export const textMimeTypes = [
  'text/plain',
  //'text/markdown'
]

export const videoMimeTypes = [
  'video/mp4',
  'video/quicktime', // .mov
]

export const audioMimeTypes = [
  'audio/mpeg', // .mp3
  'audio/wav', // .wav
  'audio/x-m4a', // .m4a
  'audio/mp4', // .m4a (alternative)
]

export const imageMimeTypes = ['image/png', 'image/jpeg', 'image/tiff']

export const acceptedFileUploadText =
  'Drag and drop files or browse for them using the button below.\r\nValid file types include: png, jpg, tiff, mp4, mov, mp3, wav, m4a, txt, pdf, pptx and docx.'
export const rejectedFileUploadText =
  'Only png, jpg, tiff, mp4, mov, mp3, wav, m4a, txt, pdf, pptx and docx files are supported.'

export const needsExtractionMimeTypes = [
  pdfMimeType,
  'text/plain',
  // docx
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  // pptx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // video files
  ...videoMimeTypes,
  // audio files
  ...audioMimeTypes,
  // image files
  ...imageMimeTypes,
]
export const supportsProgressMimeTypes = [pdfMimeType, ...videoMimeTypes, ...audioMimeTypes]
export const needsMetadataGenerationMimeTypes = [...videoMimeTypes, ...audioMimeTypes, ...imageMimeTypes]

// Using set to remove duplicates
export const contentUploadMimeTypes = [...new Set([...textMimeTypes, ...needsExtractionMimeTypes])]
// For media files, we use only MIME types (extensions cause console warnings in Dropzone)
export const mediaUploadMimeTypes = [...new Set([...videoMimeTypes, ...audioMimeTypes, ...imageMimeTypes])]
// Unified upload mime types for all file types (text, media, office docs)
export const allUploadMimeTypes = [...new Set([...contentUploadMimeTypes, ...mediaUploadMimeTypes])]

// TODO: Svgs were removed as a precaution, there was potentially an issue rendering them with iOS 17, though we didn't fully investigate.
// See SME-152 https://sidekickwellness.atlassian.net/browse/SME-152?atlOrigin=eyJpIjoiMzUwOWY5ZTU4M2NjNDM5OGE3NGU5M2ZjNGQ0MGNiOGQiLCJwIjoiaiJ9
export const ALLOWED_IMAGE_TYPES = IMAGE_MIME_TYPE.filter(type => type !== 'image/svg+xml')

export type UploadPhase = 'idle' | 'uploading' | 'processing'

export interface FileUploadProps extends Omit<DropzoneProps, 'openRef' | 'title'> {
  WrapperProps?: BoxProps
  EmptyStateProps?: Partial<EmptyStateProps>
  titles?: {
    idle?: string
    accept?: string
    reject?: string
  }
  icons?: {
    idle?: ComponentType<IconProps>
    accept?: ComponentType<IconProps>
    reject?: ComponentType<IconProps>
  }
  hint?: ReactNode
  hints?: {
    idle?: ReactNode
    accept?: ReactNode
    reject?: ReactNode
  }
  primaryActionLabel?: string
  secondaryActionLabel?: string
  onSecondaryActionClick?: () => void
  tertiaryActionLabel?: string
  onTertiaryActionClick?: () => void
  actionButtonProps?: ButtonProps
  actionHidden?: boolean
  BottomSlot?: ReactNode
  label?: string
  error?: string
  description?: string
  withAsterisk?: boolean
  // Upload phase tracking
  uploadPhase?: UploadPhase
  uploadProgress?: number
  processProgress?: number
  // Cancellation support
  onCancelClick?: () => void
  showCancelButton?: boolean
  /** @deprecated Use uploadPhase instead */
  processing?: boolean
}

export function FileUpload(props: FileUploadProps) {
  const {
    WrapperProps,
    titles,
    hint,
    hints,
    icons,
    error,
    description,
    label,
    maxFiles = 10,
    withAsterisk,
    primaryActionLabel,
    actionButtonProps,
    actionHidden,
    BottomSlot = null,
    EmptyStateProps,
    loading,
    uploadPhase = 'idle',
    uploadProgress = 0,
    processProgress = 0,
    processing, // deprecated, for backwards compatibility
    secondaryActionLabel,
    onSecondaryActionClick,
    tertiaryActionLabel,
    onTertiaryActionClick,

    // Cancellation support
    onCancelClick,
    showCancelButton,
    ...DropzoneProps
  } = props

  // Determine active phase (support deprecated 'processing' prop)
  const isUploading = uploadPhase === 'uploading'
  const isProcessing = uploadPhase === 'processing' || processing
  const showProgressOverlay = isUploading || isProcessing

  // Determine which progress value and label to show
  const progressValue = isUploading ? uploadProgress : processProgress
  const progressLabel = isUploading ? 'Uploading file...' : 'Processing content...'

  const {
    idle: IconIdle = IconCloudUpload,
    accept: IconAccept = IconArrowDown,
    reject: IconReject = IconX,
  } = icons ?? {}

  const hasControls = !actionHidden
  const defaultHint = hint ?? `Drag & drop ${inflect('file', maxFiles)} here to upload.`

  const openRef = useRef<() => void>(null)

  const idleHintContent = hints?.idle ?? defaultHint

  return (
    <Box>
      <Box
        {...WrapperProps}
        className={theme.cx(classes.wrapper, WrapperProps?.className, hasControls && classes.wrapperWControls)}>
        {label && <InputLabel label={label} withAsterisk={withAsterisk} />}

        <Box className={classes.dropzoneWrapper}>
          {showProgressOverlay && (
            <Box className={classes.progressOverlay}>
              <Box style={{ width: '100%', margin: 'auto' }}>
                <Text size="sm" fw={500} mb="sm" style={{ alignSelf: 'flex-start' }}>
                  {progressLabel}
                </Text>
                <Group gap="lg" w="100%" align="center">
                  <Progress
                    value={progressValue}
                    size="xl"
                    radius="md"
                    color={theme.colors.primaryColors[6]}
                    animated
                    style={{ flex: 1, height: '12px' }}
                  />
                  <Text size="lg" fw={600} c="dimmed" style={{ minWidth: 50, textAlign: 'right' }}>
                    {Math.round(progressValue)}%
                  </Text>
                </Group>
              </Box>
              {showCancelButton && onCancelClick && (
                <Button size="sm" variant="subtle" color="red" onClick={onCancelClick} style={{ marginTop: '12px' }}>
                  Cancel
                </Button>
              )}
            </Box>
          )}
          <Dropzone
            radius="md"
            maxFiles={maxFiles}
            {...DropzoneProps}
            loading={loading && !showProgressOverlay}
            openRef={openRef}
            classNames={{
              root: theme.cx(classes.dropzone, DropzoneProps.className, !!error && classes.dropzoneWError),
              inner: theme.cx(
                classes.dropzoneInner,
                hasControls && classes.dropzoneInnerWControls,
                DropzoneProps?.activateOnClick === false && classes.dropzoneInnerWPointerEvents,
              ),
            }}>
            <Dropzone.Idle>
              <EmptyState
                size="sm"
                title={titles?.idle ?? `Upload ${inflect('File', maxFiles)}`}
                description={idleHintContent}
                Icon={IconIdle}
                DescriptionProps={{ maw: '100%' }}
                {...EmptyStateProps}
              />
            </Dropzone.Idle>

            <Dropzone.Accept>
              <EmptyState
                size="sm"
                title={titles?.accept ?? `Drop ${inflect('File', maxFiles)} Here...`}
                description={hints?.accept ?? defaultHint}
                Icon={IconAccept}
                IconProps={{ color: theme.colors.primaryColors[6] }}
                DescriptionProps={{ maw: '100%' }}
                {...EmptyStateProps}
              />
            </Dropzone.Accept>

            <Dropzone.Reject>
              <EmptyState
                size="sm"
                title={titles?.reject ?? `Unsupported ${inflect('File', maxFiles)} Type or Size...`}
                description={hints?.reject ?? defaultHint}
                Icon={IconReject}
                IconProps={{ color: theme.colors.error }}
                DescriptionProps={{ maw: '100%' }}
                {...EmptyStateProps}
              />
            </Dropzone.Reject>

            {BottomSlot}

            {hasControls && (
              <Group gap="xl" className={theme.cx(classes.controlsWrapper, loading && classes.controlsWhileLoading)}>
                <Button className={classes.selectControl} onClick={() => openRef.current?.()} {...actionButtonProps}>
                  {primaryActionLabel ?? `Select ${inflect('File', maxFiles)}`}
                </Button>

                {secondaryActionLabel && (
                  <Button
                    size="md"
                    variant="light"
                    className={theme.cx(classes.selectControl, classes.secondaryAction)}
                    onClick={onSecondaryActionClick}
                    {...actionButtonProps}>
                    {secondaryActionLabel}
                  </Button>
                )}

                {tertiaryActionLabel && (
                  <Button
                    size="md"
                    variant="light"
                    className={theme.cx(classes.selectControl, classes.secondaryAction)}
                    onClick={onTertiaryActionClick}
                    {...actionButtonProps}>
                    {tertiaryActionLabel}
                  </Button>
                )}
              </Group>
            )}
          </Dropzone>
        </Box>
      </Box>

      {error ? (
        <Text className={classes.error}>{error}</Text>
      ) : description ? (
        <Text c="dimmed" className={classes.description}>
          {description}
        </Text>
      ) : null}
    </Box>
  )
}
